import { useEffect, useState } from "react";
import { X } from "lucide-react";
import LoadingSpinner from "src/components/Loader/LoadingSpinner";
import { useClientSignUpSteps } from "src/lib/store/hirer-steps-store";
import { useHireNowModal } from "../../../context/HireNowModalContext";
import { SimpleHttp } from "src/lib/utils/SimpleHTTP";
import StepperIndicator from "./components/stepper";
import HirerExtrasDetailsStep from "./extraDetails";
import HireNowJobTypeStep from "./jobType";
import HirerRequirementStep from "./requirements";

export default function HireNowModal({ open, closeModal, callback }) {
  const { activeStep, setActiveStep, updateUserJobType, updateUserStack, updateUserLevel, updateUserLanguages, updateUserFrameworks, updateUserSkills, setJobType, updateExtras, setBasics } = useClientSignUpSteps();
  const { editData } = useHireNowModal();
  const [stateRestored, setStateRestored] = useState(false);
  const [currentStep, updateCurrentStep] = useState(1);

  const steps = ["Job Type", "Requirements", "Extra"];

  const displayStep = (step) => {
    switch (step) {
      case 1:
        return (
          <HireNowJobTypeStep
            currentStep={currentStep}
            steps={steps}
            handleClick={handleClick}
            close={closeModal}
          />
        );
      case 2:
        return (
          <HirerRequirementStep
            currentStep={currentStep}
            steps={steps}
            handleClick={handleClick}
            close={closeModal}
          />
        );
      case 3:
        return (
          <HirerExtrasDetailsStep
            currentStep={currentStep}
            editData={editData}
            steps={steps}
            handleClick={handleClick}
            close={closeModal}
          />
        );
      default:
    }
  };

  const handleClick = (direction) => {
    let newStep = currentStep;
    direction === "next" ? newStep++ : newStep--;
    if (newStep > 0 && newStep <= steps.length) {
      updateCurrentStep(newStep);
      setActiveStep(newStep);
    }
  };

  useEffect(() => {
    if (stateRestored) return;

    updateCurrentStep(1);
    setStateRestored(true);
  }, [activeStep, stateRestored]);

  // Pre-fill store when editData is present
  useEffect(() => {
    if (!editData || !open) return

    // Derive tech/non-tech from level
    const techLevels = ['junior', 'mid', 'senior']
    const isTech = techLevels.includes((editData.level || '').toLowerCase())
    updateUserJobType(isTech ? 'tech' : 'non-tech')

    // Set level object
    const seniority = (editData.level || '').toLowerCase()
    const priceData = [
      { id: 1, seniority: 'entry level', lower_price: 15, higher_price: 25 },
      { id: 2, seniority: 'junior', lower_price: 15, higher_price: 25 },
      { id: 3, seniority: 'mid', lower_price: 15, higher_price: 25 },
      { id: 4, seniority: 'senior', lower_price: 88, higher_price: 113 },
      { id: 5, seniority: 'manager', lower_price: 150, higher_price: 200 },
      { id: 6, seniority: 'director', lower_price: 250, higher_price: 350 },
      { id: 7, seniority: 'executive', lower_price: 400, higher_price: 600 },
    ]
    const matchedLevel = priceData.find(p => p.seniority === seniority)
    if (matchedLevel) updateUserLevel(matchedLevel)

    // Restore the job's previously saved stack/role, specialties, tools & skills
    // so the client can review/refill them after a rejection.
    const restore = async () => {
      const pick = (obj, prefix) => {
        const values = []
        for (let idx = 1; idx <= 3; idx++) {
          const val = obj?.[prefix + idx]
          if (val !== null && val !== undefined && val !== '') values.push(val)
        }
        return values
      }

      const langIds = pick(editData, 'language')
      const frameIds = pick(editData, 'framework')
      const skillIds = pick(editData, 'skill')

      const resolveName = async (id, type) => {
        try {
          const res = await SimpleHttp.getNoAuth(`/api/v1/all/getCbtParamsRealname/${id}/${type}`)
          return { id: Number(id), name: res?.data ?? String(id) }
        } catch (e) {
          return { id: Number(id), name: String(id) }
        }
      }

      const [stackId, langObjs, frameObjs, skillObjs] = await Promise.all([
        (async () => {
          // Job stores the stack by NAME; the Role step selects by ID, so map name -> id.
          // Also handle legacy rows where the stack was persisted as a numeric id.
          const stackName = String(editData.stack || '').toLowerCase().trim()
          try {
            const res = await SimpleHttp.getNoAuth(`/api/v1/comp/getStacks`)
            const stacks = res?.data || []
            const match =
              // Direct id match (numeric stack id stored in DB)
              stacks.find(s => String(s.id) === stackName) ||
              // Name match
              stacks.find(s => (s.name || '').toLowerCase().trim() === stackName)
            if (match) return match.id
          } catch (e) {
            console.log('Restore stack error:', e)
          }
          // Fallback: if a numeric id was stored, pass it through as an id
          if (/^\d+$/.test(stackName)) return Number(stackName)
          return editData.stack ?? undefined
        })(),
        Promise.all(langIds.map(id => resolveName(id, 'lang'))),
        Promise.all(frameIds.map(id => resolveName(id, 'frame'))),
        Promise.all(skillIds.map(id => resolveName(id, 'skill'))),
      ])

      updateUserStack(stackId)
      updateUserLanguages(langObjs)
      updateUserFrameworks(frameObjs)
      updateUserSkills(skillObjs)
    }
    restore()

    // Set job type
    const wt = (editData.workType || editData.work_type || editData.jobDetailProjDetail?.work_type || '').toLowerCase()
    if (wt.includes('full')) setJobType('fulltime')
    else if (wt.includes('contract')) setJobType('contract')

    // Set basics
    setBasics({ jobTitle: editData.jobTitle || '' })

    /* Set extras. A job row comes back snake_case — `project_budget`,
       `payment_type` — so reading `projectBudget`/`paymentType` left the
       budget empty and silently reset every re-opened job to fixed cost.
       `jobDetails` is the brief the client typed; `jobDescription` is the URL
       of the uploaded file, so the text has to win. */
    updateExtras({
      description: editData.jobDetails || editData.jobDescription || '',
      location: editData.companyLocation || editData.jobLocation1 || '',
      no_of_talent: editData.no_of_devs || 1,
      budget:
        editData.project_budget ??
        editData.projectBudget ??
        editData.project_amount ??
        '',
      payment_type:
        editData.payment_type || editData.paymentType || 'fixedcost',
    })
  }, [editData, open])

  return (
    <div
      className={`fixed inset-0 z-50 items-center justify-center p-4 ${
        open ? 'flex' : 'hidden'
      }`}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={() => closeModal()}
        className="absolute inset-0 h-full w-full bg-brand-navy/50"
      />

      <section className="relative z-10 flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-card shadow-ui-lg">
        <div className="flex shrink-0 items-start justify-between gap-3 border-b p-5">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-brand-orange">
              Hire now
            </div>
            <h2 className="font-display text-lg font-bold text-brand-navy">Post a job</h2>
          </div>
          <button
            type="button"
            onClick={() => closeModal()}
            aria-label="Close"
            className="-mr-2 -mt-2 shrink-0 rounded-full p-2 text-brand-navy transition-colors hover:bg-surface"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {stateRestored && (
          <div className="shrink-0 border-b bg-surface px-5 py-4">
            <StepperIndicator steps={steps} currentStep={currentStep} />
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {!stateRestored ? (
            <div className="flex min-h-[40vh] items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : (
            displayStep(currentStep)
          )}
        </div>
      </section>
    </div>
  );
}
